import {GlobalAlertItem} from '../../components/GlobalAlert/types';
import {AirtableHomepageItem} from '../../models/airtable';
import {useStores} from '../../stores/use-stores';

import {useHomepage} from './useHomepage';

// Airtable free-text is not code-reviewed, so the alert-specific fields are read
// as `unknown` and normalized defensively. The middleware adds `message`/`isGlobal`
// via the DIS-1457 handoff; the client degrades gracefully when absent. Visibility
// (Start/End Date + Status) is gated server-side — the client never gates on an
// active/date field.
type AlertRow = AirtableHomepageItem & {
  message?: unknown;
  isGlobal?: unknown;
};

const normalizeMessage = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

// pageLink flows into `openPageLink` (→ `window.location.href` on web /
// `Linking.openURL` on native). Airtable free-text is not code-reviewed, so only
// accept an internal relative path (single leading `/`, not `//`) or an explicit
// http(s)/tel/mailto URL. Drop protocol-relative (`//evil`) and dangerous schemes
// (`javascript:`, `data:`, …) to `undefined`.
const sanitizePageLink = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const isInternalPath = trimmed.startsWith('/') && !trimmed.startsWith('//');
  const isAllowedProtocol = /^(https?|tel|mailto):/i.test(trimmed);
  return isInternalPath || isAllowedProtocol ? trimmed : undefined;
};

/**
 * Selector over the shared `useHomepage` query (react-query dedupes → no extra
 * network call). Reads the `AlertBanner` bucket and returns a normalized
 * `GlobalAlertItem` for the *text* alert, or `null` when there is nothing to
 * show. Image-only rows (no message) render inline on the homepage instead
 * (see HomepageScreen) — this selector returns null for those. Never throws.
 */
export function useGlobalAlert(): GlobalAlertItem | null {
  const {shopSelectionStore} = useStores();
  const selectedStore = shopSelectionStore?.selectedStore;

  const {data: homepage} = useHomepage({
    storeNumber: selectedStore?.store_number,
    storeState: selectedStore?.address?.administrative_area?.toUpperCase(),
  });

  const raw = homepage?.data?.AlertBanner as AlertRow | undefined;
  if (!raw) {
    return null;
  }

  const message = normalizeMessage(raw.message);
  // Image-only alerts render inline on the homepage (see HomepageScreen);
  // the global strip is text-only.
  if (!message) {
    return null;
  }

  const isGlobal = raw.isGlobal === true;
  const pageLink = sanitizePageLink(raw.pageLink);
  return {message, isGlobal, pageLink};
}
